import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    IconButton,
    TextField,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Checkbox,
    Tooltip,
    CircularProgress,
    InputAdornment,
    Portal,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import AssetService from '../../../../Services/assets.service';
import { beautifyNumber } from '../../../../Utils/FormatNumber';
import {
    getAssetId,
    mapAssetToReplacementRow,
    mapSavedReplacementToRow,
    getBeforeReplacementCost,
    computeAfterReplacementCost,
    computeReplacementTotals,
    clampReplacementRowField,
    buildReplacementSaveRows,
    getAvailableRealGainForReplacement,
    getAvailableInflationaryGainForReplacement,
    computeSection96Limits,
    getReplacementPurchaseWindow,
    isWithinReplacementWindow,
} from '../../../../Utils/capitalGainCalculations';
import modalStyles from '../../../JournalEntryies/SubComponents/JournalEntryTemplatesModal.module.scss';
import styles from './CapitalGainReplacementModal.module.scss';

const REPLACEMENT_VALIDATION_MESSAGES = {
    replacementNegative: 'סכום השחלוף לא יכול להיות שלילי',
    exceedsBeforeBasis: 'סכום השחלוף לא יכול להיות גבוה מהיתרה לפני שחלוף',
    realExceedsGain: 'סכום שחלוף ריאלי גבוה מהרווח הריאלי הזמין',
    inflationaryExceedsGain: "סכום שחלוף אינפ' חייב גבוה מהסכום האינפלציוני החייב",
    exceedsSection96Limit: 'סכום השחלוף גבוה מהתקרה לפי סעיף 96 (רווח ההון פחות עודף התמורה על מחיר הנכסים החלופיים)',
};

const formatDateHe = (d) =>
    !d
        ? '—'
        : new Date(d).toLocaleDateString('he-IL', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
          });

const normalizeText = (value) =>
    String(value ?? '')
        .toLowerCase()
        .trim();

const isRealReplacementAmountInvalid = (value) => {
    if (value === '' || value === null || value === undefined) return true;
    return Number(value) <= 0;
};

const getReplacementRowKey = (row) =>
    row?.id ||
    row?.replacementId ||
    row?.assetId ||
    row?.replacementAssetId ||
    row?.ReplacementAssetId;

const inputFieldSx = {
    width: 110,
    '& .MuiInputBase-root': { height: 28, backgroundColor: '#fff' },
    '& .MuiInputBase-input': {
        py: 0,
        px: 0.75,
        fontSize: 12.5,
        textAlign: 'center',
        direction: 'ltr',
    },
};

const cellSx = { fontSize: 12.5, py: 0.6, px: 1 };

const SELECTED_COLUMNS = [
    { key: 'year', width: 64 },
    { key: 'assetNumber', width: 90 },
    { key: 'assetName', width: 'auto' },
    { key: 'purchaseDate', width: 104 },
    { key: 'originalPrice', width: 110 },
    { key: 'before', width: 120 },
    { key: 'real', width: 134 },
    { key: 'inflationary', width: 134 },
    { key: 'after', width: 120 },
    { key: 'actions', width: 52 },
];

const CANDIDATE_COLUMNS = [
    { key: 'select', width: 52 },
    { key: 'year', width: 64 },
    { key: 'assetNumber', width: 100 },
    { key: 'assetName', width: 'auto' },
    { key: 'purchaseDate', width: 110 },
    { key: 'originalPrice', width: 120 },
    { key: 'reduction', width: 130 },
];

const renderColGroup = (columns) => (
    <colgroup>
        {columns.map((col) => (
            <col key={col.key} style={col.width === 'auto' ? undefined : { width: col.width }} />
        ))}
    </colgroup>
);

const CapitalGainReplacementModal = ({
    open = false,
    onClose = () => {},
    onApply = () => {},
    direction = 'rtl',
    caseId,
    token,
    excludedAssetId = null,
    soldAssetId = null,
    initialReplacementRows = [],
    canApplyReplacement = true,
    maxRealGain = 0,
    maxInflationaryGain = 0,
    gains = null,
    rates = null,
    saleDate = null,
    salePrice = 0,
    parentDepreciationPercentage = 0,
}) => {
    const { t } = useTranslation();
    const isRtl = direction === 'rtl';

    const [candidateAssets, setCandidateAssets] = useState([]);
    const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
    const [replacementRows, setReplacementRows] = useState([]);
    const [loadingCandidates, setLoadingCandidates] = useState(false);
    const [loadingSaved, setLoadingSaved] = useState(false);
    const [saving, setSaving] = useState(false);
    const [replacementSearchTerm, setReplacementSearchTerm] = useState('');
    const [realReplacementFieldErrors, setRealReplacementFieldErrors] = useState(new Set());
    const [validationMessage, setValidationMessage] = useState('');

    const reductionContext = useMemo(
        () => ({ saleDate, parentDepreciationPercentage }),
        [saleDate, parentDepreciationPercentage]
    );

    const fetchReplacementCandidates = useCallback(async () => {
        if (!caseId || !token || !soldAssetId) {
            setCandidateAssets([]);
            return;
        }

        setLoadingCandidates(true);
        try {
            const response = await AssetService.GetCapitalGainReplacementCandidates(
                caseId,
                soldAssetId,
                token
            );
            if (!response?.success) {
                throw new Error(response?.error || t('FailedToFetchAssets'));
            }
            setCandidateAssets(response.data || []);
            setSelectedCandidateIds([]);
        } catch (error) {
            toast.error(error?.message || t('FailedToFetchAssets'));
            setCandidateAssets([]);
        } finally {
            setLoadingCandidates(false);
        }
    }, [caseId, token, soldAssetId, t]);

    useEffect(() => {
        if (!open) return;

        let cancelled = false;
        setSelectedCandidateIds([]);
        setReplacementSearchTerm('');
        setRealReplacementFieldErrors(new Set());
        setValidationMessage('');

        const loadSaved = async () => {
            setLoadingSaved(true);
            try {
                if (soldAssetId && caseId && token) {
                    const response = await AssetService.GetCapitalGainReplacements(
                        caseId,
                        soldAssetId,
                        token
                    );
                    if (cancelled) return;
                    if (response?.success && Array.isArray(response.data) && response.data.length > 0) {
                        setReplacementRows(
                            response.data.map((row) => mapSavedReplacementToRow(row, reductionContext))
                        );
                        return;
                    }
                }

                setReplacementRows(
                    (initialReplacementRows || []).map((row) =>
                        row.assetId != null ? row : mapAssetToReplacementRow(row, reductionContext)
                    )
                );
            } catch (error) {
                if (!cancelled) {
                    toast.error(error?.message || t('FailedToLoadCapitalGainData'));
                    setReplacementRows(
                        (initialReplacementRows || []).map((row) =>
                            row.assetId != null ? row : mapAssetToReplacementRow(row, reductionContext)
                        )
                    );
                }
            } finally {
                if (!cancelled) setLoadingSaved(false);
            }
        };

        loadSaved();

        return () => {
            cancelled = true;
        };
    }, [open, soldAssetId, caseId, token, initialReplacementRows, reductionContext, t]);

    useEffect(() => {
        if (!open || !caseId || !soldAssetId) return;
        fetchReplacementCandidates();
    }, [open, caseId, soldAssetId, fetchReplacementCandidates]);

    const selectedReplacementIds = useMemo(
        () => new Set(replacementRows.map((row) => row.assetId)),
        [replacementRows]
    );

    const filteredCandidateAssets = useMemo(() => {
        const search = normalizeText(replacementSearchTerm);
        if (!search) return candidateAssets;

        return candidateAssets.filter((asset) => {
            const assetNumber = normalizeText(asset.assetNumber ?? asset.AssetNumber);
            const name = normalizeText(asset.assetName ?? asset.AssetName ?? asset.name ?? asset.Name);
            const description = normalizeText(asset.description ?? asset.Description);
            const assetGroupId = normalizeText(asset.assetGroupId ?? asset.AssetGroupId);

            return (
                assetNumber.includes(search) ||
                name.includes(search) ||
                description.includes(search) ||
                assetGroupId.includes(search)
            );
        });
    }, [candidateAssets, replacementSearchTerm]);

    const availableCandidates = useMemo(
        () =>
            filteredCandidateAssets.filter((asset) => !selectedReplacementIds.has(getAssetId(asset))),
        [filteredCandidateAssets, selectedReplacementIds]
    );

    const totals = useMemo(() => computeReplacementTotals(replacementRows), [replacementRows]);

    const availableRealGainForReplacement = useMemo(
        () => getAvailableRealGainForReplacement(gains),
        [gains]
    );

    const availableInflationaryGainForReplacement = useMemo(
        () => getAvailableInflationaryGainForReplacement(gains),
        [gains]
    );

    const section96 = useMemo(
        () => computeSection96Limits({ gains, salePrice, replacementRows }),
        [gains, salePrice, replacementRows]
    );

    const purchaseWindow = useMemo(() => getReplacementPurchaseWindow(saleDate), [saleDate]);

    const section96PriceMessage = t('ReplacementPriceBelowRemainingCost', {
        price: beautifyNumber(section96.replacementPrice, 0),
        remaining: beautifyNumber(section96.soldRemainingCost, 0),
        defaultValue:
            'מחיר הנכסים החלופיים ({{price}}) חייב לעלות על יתרת המחיר המקורי של הנכס שנמכר ({{remaining}}) (סעיף 96)',
    });

    useEffect(() => {
        if (!open) return;

        setReplacementRows((prev) => {
            let changed = false;
            const next = prev.map((row) => {
                let updated = row;
                if (
                    availableRealGainForReplacement <= 0 &&
                    Number(row.realReplacementAmount || 0) !== 0
                ) {
                    updated = { ...updated, realReplacementAmount: 0 };
                    changed = true;
                }
                if (
                    availableInflationaryGainForReplacement <= 0 &&
                    Number(row.inflationaryReplacementAmount || 0) !== 0
                ) {
                    updated = { ...updated, inflationaryReplacementAmount: 0 };
                    changed = true;
                }
                return updated;
            });
            return changed ? next : prev;
        });
    }, [open, availableRealGainForReplacement, availableInflationaryGainForReplacement]);

    const handleToggleCandidate = (assetId) => {
        setSelectedCandidateIds((prev) =>
            prev.includes(assetId)
                ? prev.filter((id) => id !== assetId)
                : [...prev, assetId]
        );
    };

    const handleMoveSelectedToReplacement = () => {
        const selectedAssets = availableCandidates.filter((asset) =>
            selectedCandidateIds.includes(getAssetId(asset))
        );

        setReplacementRows((prev) => {
            const existingIds = new Set(prev.map((row) => row.assetId));
            const newRows = selectedAssets
                .filter((asset) => !existingIds.has(getAssetId(asset)))
                .map((asset) => mapAssetToReplacementRow(asset, reductionContext));
            return [...prev, ...newRows];
        });

        setSelectedCandidateIds([]);
    };

    const handleRemoveReplacementRow = async (row) => {
        const assetName = row.assetName || row.name || row.Name || '';
        const assetNumber = row.assetNumber || row.AssetNumber || '';

        const result = await Swal.fire({
            title: 'האם אתה בטוח?',
            text: `האם להסיר את נכס השחלוף ${assetNumber ? `${assetNumber} - ` : ''}${assetName}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'כן, הסר',
            cancelButtonText: 'ביטול',
            reverseButtons: true,
        });

        if (!result.isConfirmed) return;

        try {
            const replacementId = row.id || row.replacementId;
            if (replacementId && caseId && token) {
                const response = await AssetService.DeleteCapitalGainReplacement(
                    caseId,
                    replacementId,
                    token
                );
                if (!response?.success) {
                    throw new Error(response?.error || t('FailedToSaveCapitalGainReplacements'));
                }
            }

            const nextRows = replacementRows.filter(
                (item) => getReplacementRowKey(item) !== getReplacementRowKey(row)
            );
            setReplacementRows(nextRows);

            const nextTotals = computeReplacementTotals(nextRows);
            onApply({
                selectedAssets: nextRows,
                realReplacementAmount: nextTotals.totalRealReplacementAmount,
                inflationaryReplacementAmount: nextTotals.totalInflationaryReplacementAmount,
            });

            toast.success('נכס השחלוף הוסר בהצלחה');
        } catch (error) {
            console.error('Failed to remove replacement asset:', error);
            toast.error('שגיאה בהסרת נכס השחלוף');
        }
    };

    const handleRowAmountChange = (assetId, field, rawValue) => {
        if (field === 'realReplacementAmount') {
            setRealReplacementFieldErrors((prev) => {
                if (!prev.has(assetId)) return prev;
                if (isRealReplacementAmountInvalid(rawValue)) return prev;
                const next = new Set(prev);
                next.delete(assetId);
                return next;
            });
        }

        const row = replacementRows.find((item) => item.assetId === assetId);
        if (!row) return;
        const { errorCode, ...values } = clampReplacementRowField({
            row,
            field,
            rawValue,
            replacementRows,
            maxRealGain,
            maxInflationaryGain,
            maxTotalDeferral: section96.maxDeferral,
        });
        setValidationMessage(
            errorCode
                ? t(errorCode, { defaultValue: REPLACEMENT_VALIDATION_MESSAGES[errorCode] || errorCode })
                : ''
        );
        setReplacementRows((prev) =>
            prev.map((item) => (item.assetId === assetId ? { ...item, ...values } : item))
        );
    };

    const handleApply = async () => {
        if (!caseId || !token || !soldAssetId) {
            toast.error(t('FailedToSaveCapitalGainReplacements') || 'שמירת שחלוף נכסים נכשלה');
            return;
        }

        const invalidRealReplacementIds =
            availableRealGainForReplacement > 0
                ? replacementRows
                      .filter((row) => isRealReplacementAmountInvalid(row.realReplacementAmount))
                      .map((row) => row.assetId)
                      .filter(Boolean)
                : [];

        if (invalidRealReplacementIds.length > 0) {
            setRealReplacementFieldErrors(new Set(invalidRealReplacementIds));
            toast.error(
                t('RealReplacementAmountRequired') ||
                    'יש להזין סכום שחלוף ריאלי גדול מאפס לכל נכס שנבחר'
            );
            return;
        }

        setRealReplacementFieldErrors(new Set());

        if (totals.totalRealReplacementAmount > availableRealGainForReplacement) {
            toast.error('סכום שחלוף ריאלי גבוה מהרווח הריאלי החייב');
            return;
        }

        if (totals.totalInflationaryReplacementAmount > availableInflationaryGainForReplacement) {
            toast.error('סכום שחלוף אינפ׳ חייב גבוה מהסכום האינפלציוני החייב');
            return;
        }

        if (totals.totalReplacement > 0 && !section96.priceEligible) {
            toast.error(section96PriceMessage);
            return;
        }

        if (totals.totalReplacement > section96.maxDeferral) {
            toast.error(
                t('exceedsSection96Limit', { defaultValue: REPLACEMENT_VALIDATION_MESSAGES.exceedsSection96Limit })
            );
            return;
        }

        const taxYear = saleDate ? new Date(saleDate).getFullYear() : new Date().getFullYear();
        const payload = {
            soldAssetId,
            taxYear,
            rows: buildReplacementSaveRows(replacementRows, gains, rates),
        };

        setSaving(true);
        try {
            const response = await AssetService.SaveCapitalGainReplacements(caseId, payload, token);
            if (!response?.success) {
                throw new Error(response?.error || t('FailedToSaveCapitalGainReplacements'));
            }

            const savedRows = (response.data || []).map((row) =>
                mapSavedReplacementToRow(row, reductionContext)
            );
            const savedTotals = computeReplacementTotals(savedRows);

            setReplacementRows(savedRows);
            onApply({
                selectedAssets: savedRows,
                realReplacementAmount: savedTotals.totalRealReplacementAmount,
                inflationaryReplacementAmount: savedTotals.totalInflationaryReplacementAmount,
            });
            toast.success('נתוני השחלוף נשמרו בהצלחה');
        } catch (error) {
            console.error('Failed to apply asset replacement:', error);
            toast.error(error?.message || t('FailedToSaveCapitalGainReplacements') || 'שגיאה בשמירת נתוני השחלוף');
        } finally {
            setSaving(false);
        }
    };

    const renderEmptyRow = (message, colSpan) => (
        <TableRow>
            <TableCell colSpan={colSpan} className={modalStyles.actionsCell} sx={{ ...cellSx, color: '#999', py: 2.5 }}>
                {message}
            </TableCell>
        </TableRow>
    );

    if (!open) return null;

    const hints = [
        availableRealGainForReplacement <= 0 &&
            (t('NoRealGainForReplacement', { defaultValue: 'אין רווח ריאלי חייב לשחלוף' })),
        availableInflationaryGainForReplacement <= 0 &&
            (t('NoInflationaryGainForReplacement', { defaultValue: 'אין סכום אינפ׳ חייב לשחלוף' })),
        purchaseWindow &&
            t('ReplacementPurchaseWindowHint', {
                from: formatDateHe(purchaseWindow.from),
                to: formatDateHe(purchaseWindow.to),
                defaultValue:
                    'לפי סעיף 96 מוצגים רק נכסים שנרכשו בין {{from}} ל-{{to}} (4 חודשים לפני המכירה עד 12 חודשים אחריה)',
            }),
        replacementRows.length > 0 && !section96.priceEligible && section96PriceMessage,
        purchaseWindow &&
            replacementRows.some((row) => !isWithinReplacementWindow(row.purchaseDate, saleDate)) &&
            t('ReplacementOutsidePurchaseWindow', {
                defaultValue: 'חלק מנכסי השחלוף שנבחרו נרכשו מחוץ לחלון של סעיף 96 — יש להסיר אותם',
            }),
        validationMessage,
    ].filter(Boolean);

    const numCell = `${modalStyles.actionsCell} ${styles.num}`;

    return (
        <Portal>
            <div className={`${modalStyles.overlay} ${isRtl ? modalStyles.rtl : modalStyles.ltr}`}>
                <div className={`${modalStyles.modal} ${modalStyles.modalEdit}`} role="dialog" aria-modal="true" dir={direction}>
                    <div className={modalStyles.body}>
                        <div className={modalStyles.header}>
                            <span>{t('ReplacementAssetsPickerTitle') || 'שחלוף נכסים'}</span>
                            <Tooltip title={t('Close') || 'סגור'}>
                                <IconButton onClick={onClose} className={modalStyles.closeIcon}>
                                    <CancelIcon className={modalStyles.closeIcon} />
                                </IconButton>
                            </Tooltip>
                        </div>

                        <div className={modalStyles.scroll}>
                            {!canApplyReplacement ? (
                                <div className={modalStyles.emptyState}>
                                    {t('NoTaxableGainForReplacement') || 'אין רווח חייב לשחלוף'}
                                </div>
                            ) : (
                                <>
                                    <div className={styles.summaryBar}>
                                        <span className={`${styles.chip} ${styles.chipPrimary}`}>
                                            {t('TotalReplacement') || 'סה"כ שחלוף'}
                                            <b>{beautifyNumber(totals.totalReplacement, 0)}</b>
                                        </span>
                                        <span className={styles.chip}>
                                            {t('TotalRealReplacement') || 'סה"כ שחלוף ריאלי'}
                                            <b>
                                                {beautifyNumber(totals.totalRealReplacementAmount, 0)} /{' '}
                                                {beautifyNumber(availableRealGainForReplacement, 0)}
                                            </b>
                                        </span>
                                        <span className={styles.chip}>
                                            {t('TotalInflationaryReplacement') || "סה\"כ שחלוף אינפ' חייב"}
                                            <b>
                                                {beautifyNumber(totals.totalInflationaryReplacementAmount, 0)} /{' '}
                                                {beautifyNumber(availableInflationaryGainForReplacement, 0)}
                                            </b>
                                        </span>
                                        {replacementRows.length > 0 && (
                                            <>
                                                <span className={styles.chip}>
                                                    {t('ReplacementAssetsPrice', { defaultValue: 'מחיר הנכסים החלופיים' })}
                                                    <b>{beautifyNumber(section96.replacementPrice, 0)}</b>
                                                </span>
                                                <span className={styles.chip}>
                                                    {t('ReplacementTaxableExcess', { defaultValue: 'עודף תמורה חייב במס' })}
                                                    <b>{beautifyNumber(section96.taxableExcess, 0)}</b>
                                                </span>
                                                <span className={`${styles.chip} ${styles.chipPrimary}`}>
                                                    {t('ReplacementSection96Limit', { defaultValue: 'תקרת שחלוף (ס׳ 96)' })}
                                                    <b>{beautifyNumber(section96.maxDeferral, 0)}</b>
                                                </span>
                                            </>
                                        )}
                                    </div>

                                    {hints.length > 0 && (
                                        <div className={styles.hints}>
                                            {hints.map((hint) => (
                                                <p key={hint} className={styles.hint}>{hint}</p>
                                            ))}
                                        </div>
                                    )}

                                    <div className={styles.section}>
                                        <p className={modalStyles.sectionTitle}>
                                            {t('SelectedReplacementAssets') || 'נכסים שנבחרו לשחלוף'}
                                        </p>
                                        <div className={`${modalStyles.tableWrap} ${styles.relative}`}>
                                            {loadingSaved && (
                                                <div className={styles.loadingCover}>
                                                    <CircularProgress size={26} />
                                                </div>
                                            )}
                                            <TableContainer sx={{ maxHeight: 280, overflow: 'auto' }}>
                                                <Table size="small" stickyHeader sx={{ minWidth: 1080, tableLayout: 'fixed' }}>
                                                    {renderColGroup(SELECTED_COLUMNS)}
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={cellSx}>{t('Year') || 'שנה'}</TableCell>
                                                            <TableCell sx={cellSx}>{t('AssetNumber') || 'מס׳ נכס'}</TableCell>
                                                            <TableCell sx={cellSx}>{t('AssetName') || 'שם הנכס'}</TableCell>
                                                            <TableCell sx={cellSx}>{t('PurchaseDate') || 'תאריך רכישה'}</TableCell>
                                                            <TableCell sx={cellSx} className={modalStyles.actionsCell}>{t('OriginalPrice') || 'מחיר מקורי'}</TableCell>
                                                            <TableCell sx={cellSx} className={modalStyles.actionsCell}>
                                                                {t('BeforeCurrentReplacement') || 'לפני שחלוף נוכחי'}
                                                            </TableCell>
                                                            <TableCell sx={cellSx} className={modalStyles.actionsCell}>
                                                                {t('RealReplacementAmount') || 'סכום שחלוף ריאלי'}
                                                            </TableCell>
                                                            <TableCell sx={cellSx} className={modalStyles.actionsCell}>
                                                                {t('InflationaryReplacementAmount') || "סכום שחלוף אינפ' חייב"}
                                                            </TableCell>
                                                            <TableCell sx={cellSx} className={modalStyles.actionsCell}>
                                                                {t('AfterCurrentReplacement') || 'אחרי שחלוף נוכחי'}
                                                            </TableCell>
                                                            <TableCell sx={cellSx} className={modalStyles.actionsCell} />
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {replacementRows.length === 0
                                                            ? renderEmptyRow(
                                                                  t('NoSelectedReplacementAssets') || 'לא נבחרו נכסים לשחלוף',
                                                                  SELECTED_COLUMNS.length
                                                              )
                                                            : replacementRows.map((row) => (
                                                                  <TableRow key={row.assetId} hover>
                                                                      <TableCell sx={cellSx}>{row.year ?? '—'}</TableCell>
                                                                      <TableCell sx={cellSx}>{row.assetNumber ?? '—'}</TableCell>
                                                                      <TableCell sx={cellSx}>
                                                                          <Tooltip title={row.assetName || ''} disableHoverListener={!row.assetName}>
                                                                              <div className={styles.name}>{row.assetName ?? '—'}</div>
                                                                          </Tooltip>
                                                                      </TableCell>
                                                                      <TableCell sx={cellSx}>{formatDateHe(row.purchaseDate)}</TableCell>
                                                                      <TableCell sx={cellSx} className={numCell}>
                                                                          {beautifyNumber(row.originalPrice, 0)}
                                                                      </TableCell>
                                                                      <TableCell sx={cellSx} className={numCell}>
                                                                          {beautifyNumber(getBeforeReplacementCost(row), 0)}
                                                                      </TableCell>
                                                                      <TableCell sx={cellSx} className={modalStyles.actionsCell}>
                                                                          <TextField
                                                                              size="small"
                                                                              type="number"
                                                                              value={row.realReplacementAmount ?? 0}
                                                                              error={realReplacementFieldErrors.has(row.assetId)}
                                                                              disabled={availableRealGainForReplacement <= 0}
                                                                              onChange={(e) =>
                                                                                  handleRowAmountChange(row.assetId, 'realReplacementAmount', e.target.value)
                                                                              }
                                                                              inputProps={{ min: 0 }}
                                                                              sx={inputFieldSx}
                                                                          />
                                                                      </TableCell>
                                                                      <TableCell sx={cellSx} className={modalStyles.actionsCell}>
                                                                          <TextField
                                                                              size="small"
                                                                              type="number"
                                                                              value={row.inflationaryReplacementAmount ?? 0}
                                                                              disabled={availableInflationaryGainForReplacement <= 0}
                                                                              onChange={(e) =>
                                                                                  handleRowAmountChange(row.assetId, 'inflationaryReplacementAmount', e.target.value)
                                                                              }
                                                                              inputProps={{ min: 0 }}
                                                                              sx={inputFieldSx}
                                                                          />
                                                                      </TableCell>
                                                                      <TableCell sx={cellSx} className={`${numCell} ${styles.strong}`}>
                                                                          {beautifyNumber(computeAfterReplacementCost(row), 0)}
                                                                      </TableCell>
                                                                      <TableCell sx={cellSx} className={modalStyles.actionsCell}>
                                                                          <Tooltip title={t('Remove') || 'הסר'}>
                                                                              <IconButton
                                                                                  size="small"
                                                                                  color="error"
                                                                                  onClick={() => handleRemoveReplacementRow(row)}
                                                                              >
                                                                                  <DeleteOutlineIcon fontSize="small" />
                                                                              </IconButton>
                                                                          </Tooltip>
                                                                      </TableCell>
                                                                  </TableRow>
                                                              ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </div>
                                    </div>

                                    <div className={styles.moveRow}>
                                        <Tooltip title={t('MoveToReplacement') || 'העלה לשחלוף'}>
                                            <span>
                                                <button
                                                    type="button"
                                                    className={styles.moveBtn}
                                                    onClick={handleMoveSelectedToReplacement}
                                                    disabled={selectedCandidateIds.length === 0}
                                                >
                                                    <ArrowUpwardIcon />
                                                </button>
                                            </span>
                                        </Tooltip>
                                    </div>

                                    <div className={styles.section}>
                                        <div className={styles.sectionHeader}>
                                            <p className={modalStyles.sectionTitle}>
                                                {t('PotentialReplacementAssets') || 'נכסים פוטנציאליים לשחלוף'}
                                            </p>
                                            <TextField
                                                placeholder={t('Search')}
                                                value={replacementSearchTerm}
                                                onChange={(e) => setReplacementSearchTerm(e.target.value)}
                                                variant="standard"
                                                className={styles.search}
                                                InputProps={{
                                                    disableUnderline: true,
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                inputProps={{ style: { textAlign: isRtl ? 'right' : 'left' } }}
                                            />
                                        </div>
                                        <div className={`${modalStyles.tableWrap} ${styles.relative}`}>
                                            {loadingCandidates && (
                                                <div className={styles.loadingCover}>
                                                    <CircularProgress size={26} />
                                                </div>
                                            )}
                                            <TableContainer sx={{ maxHeight: 320, overflow: 'auto' }}>
                                                <Table size="small" stickyHeader sx={{ minWidth: 820, tableLayout: 'fixed' }}>
                                                    {renderColGroup(CANDIDATE_COLUMNS)}
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={cellSx} className={modalStyles.actionsCell} />
                                                            <TableCell sx={cellSx}>{t('Year') || 'שנה'}</TableCell>
                                                            <TableCell sx={cellSx}>{t('AssetNumber') || 'מספר נכס'}</TableCell>
                                                            <TableCell sx={cellSx}>{t('AssetName') || 'שם הנכס'}</TableCell>
                                                            <TableCell sx={cellSx}>{t('PurchaseDate') || 'תאריך רכישה'}</TableCell>
                                                            <TableCell sx={cellSx} className={modalStyles.actionsCell}>{t('OriginalPrice') || 'עלות'}</TableCell>
                                                            <TableCell sx={cellSx} className={modalStyles.actionsCell}>
                                                                {t('ReductionBalance') || 'יתרה להפחתה'}
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {availableCandidates.length === 0
                                                            ? renderEmptyRow(
                                                                  t('NoPotentialReplacementAssets') ||
                                                                      'לא נמצאו נכסים פוטנציאליים לשחלוף',
                                                                  CANDIDATE_COLUMNS.length
                                                              )
                                                            : availableCandidates.map((asset) => {
                                                                  const assetId = getAssetId(asset);
                                                                  const row = mapAssetToReplacementRow(asset, reductionContext);
                                                                  const isSelected = selectedCandidateIds.includes(assetId);
                                                                  return (
                                                                      <TableRow
                                                                          key={assetId}
                                                                          hover
                                                                          onClick={() => handleToggleCandidate(assetId)}
                                                                          className={isSelected ? styles.selectedRow : undefined}
                                                                          sx={{ cursor: 'pointer' }}
                                                                      >
                                                                          <TableCell sx={cellSx} className={modalStyles.actionsCell} padding="checkbox">
                                                                              <Checkbox
                                                                                  size="small"
                                                                                  checked={isSelected}
                                                                                  onChange={() => handleToggleCandidate(assetId)}
                                                                                  onClick={(e) => e.stopPropagation()}
                                                                              />
                                                                          </TableCell>
                                                                          <TableCell sx={cellSx}>{row.year ?? '—'}</TableCell>
                                                                          <TableCell sx={cellSx}>{row.assetNumber ?? '—'}</TableCell>
                                                                          <TableCell sx={cellSx}>
                                                                              <Tooltip title={row.assetName || ''} disableHoverListener={!row.assetName}>
                                                                                  <div className={styles.name}>{row.assetName ?? '—'}</div>
                                                                              </Tooltip>
                                                                          </TableCell>
                                                                          <TableCell sx={cellSx}>{formatDateHe(row.purchaseDate)}</TableCell>
                                                                          <TableCell sx={cellSx} className={numCell}>
                                                                              {beautifyNumber(row.originalPrice, 0)}
                                                                          </TableCell>
                                                                          <TableCell sx={cellSx} className={`${numCell} ${styles.strong}`}>
                                                                              {beautifyNumber(row.remainingCost, 0)}
                                                                          </TableCell>
                                                                      </TableRow>
                                                                  );
                                                              })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className={modalStyles.footer}>
                            <button type="button" className={modalStyles.secondaryBtn} onClick={onClose}>
                                {t('Cancel') || 'ביטול'}
                            </button>
                            <button
                                type="button"
                                className={modalStyles.primaryBtn}
                                onClick={handleApply}
                                disabled={!canApplyReplacement || saving || loadingSaved}
                            >
                                {saving ? <CircularProgress size={18} color="inherit" /> : t('Apply') || 'אישור'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default CapitalGainReplacementModal;
